/**
 * Section for displaying and editing bar and other revenue
 */

import React from 'react';
import { Box, Typography, TextField, InputAdornment, Grid, Divider, Stack } from '@mui/material';
import { formatCurrency } from '../../../utils/formatters';
import { FinancialLine } from './FinancialLine';

interface OtherRevenueSectionProps {
  barRevenue: number;
  otherRevenue: number;
  isEditing: boolean;
  isDatePast: boolean;
  formData: Record<string, unknown>;
  onUpdateField: (field: string, value: unknown) => void;
  onStartEdit?: (section: string, holdId: string) => Promise<void>;
  holdId?: string;
}

export const OtherRevenueSection: React.FC<OtherRevenueSectionProps> = ({
  barRevenue,
  otherRevenue,
  isEditing,
  isDatePast,
  formData,
  onUpdateField,
  onStartEdit,
  holdId,
}) => {
  const displayBarRevenue = isEditing
    ? Number(formData.barRevenue ?? barRevenue)
    : barRevenue;
  const displayOtherRevenue = isEditing
    ? Number(formData.otherRevenue ?? otherRevenue)
    : otherRevenue;

  const totalOtherRevenue = displayBarRevenue + displayOtherRevenue;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Other Revenue (This Date)
      </Typography>

      {isEditing ? (
        // Edit mode
        <Grid container spacing={2}>
          {!isDatePast && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary">
                Bar and other revenue are entered after the show.
              </Typography>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Bar Revenue"
              type="number"
              value={displayBarRevenue || ''}
              onChange={(e) => onUpdateField('barRevenue', parseFloat(e.target.value) || 0)}
              disabled={!isDatePast}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Other Revenue"
              type="number"
              value={displayOtherRevenue || ''}
              onChange={(e) => onUpdateField('otherRevenue', parseFloat(e.target.value) || 0)}
              disabled={!isDatePast}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
            <FinancialLine label="Total Other Revenue:" value={formatCurrency(totalOtherRevenue)} total />
          </Grid>
        </Grid>
      ) : (
        // View mode
        <Stack
          spacing={1}
          onClick={() => {
            if (onStartEdit && holdId) {
              void onStartEdit('financial', holdId);
            }
          }}
          sx={{
            cursor: onStartEdit ? 'pointer' : 'default',
            '&:hover': onStartEdit ? {
              bgcolor: 'action.hover',
              borderRadius: 1,
            } : {},
            p: 1,
            mx: -1,
          }}
        >
          <FinancialLine label="Bar Revenue" value={formatCurrency(barRevenue)} />

          <FinancialLine label="Other Revenue" value={formatCurrency(otherRevenue)} />

          <Divider sx={{ my: 2 }} />

          <FinancialLine label="Total Other Revenue:" value={formatCurrency(totalOtherRevenue)} total />
        </Stack>
      )}
    </Box>
  );
};
